import { CSSProperties, forwardRef } from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';
import cn from 'classnames';

import {
  ButtonRole,
  getRoleButtonSx,
  StyledButtonSx,
  TonalButtonSx,
  TonalVariantButtonSx,
  WArningButtonSx,
} from './Button.styles';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  iconOnly?: boolean;
  hover?: boolean;
  active?: boolean;
  loading?: boolean;
  color?: 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'tonal' | 'tonal variant' | 'warning';
  variant?: 'text' | 'contained';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, iconOnly, hover, active, loading, disabled, variant, color, ...rest }, ref) => {
    const props = {
      className: cn({
        '-hover': hover,
        '-active': active,
      }),
      disabled: disabled || loading,
    };

    if (!iconOnly && color !== 'tonal' && color !== 'tonal variant' && color !== 'warning') {
      const { sx, className, style, onClick, ...buttonProps } = rest;
      const role = getButtonRole(color, variant);
      const muiVariant = roleVariants[role];

      return (
        <MuiButton
          {...buttonProps}
          {...props}
          ref={ref}
          color="primary"
          variant={muiVariant}
          className={cn(props.className, className)}
          style={getGeometryStyle(style)}
          sx={[...(Array.isArray(sx) ? sx : [sx]), getRoleButtonSx(role)]}
          onClick={(event) => {
            if (disabled || loading) {
              event.preventDefault();
              event.stopPropagation();

              return;
            }
            onClick?.(event);
          }}
        >
          {children}
        </MuiButton>
      );
    }

    switch (color) {
      case 'tonal':
        return (
          <MuiButton sx={TonalButtonSx} {...props} variant="contained" {...rest} ref={ref}>
            {children}
          </MuiButton>
        );
      case 'tonal variant':
        return (
          <MuiButton sx={TonalVariantButtonSx} {...props} variant="contained" {...rest} ref={ref}>
            {children}
          </MuiButton>
        );
      case 'warning':
        return (
          <MuiButton sx={WArningButtonSx} {...props} variant="contained" {...rest} ref={ref}>
            {children}
          </MuiButton>
        );
      default:
        return (
          <MuiButton
            sx={StyledButtonSx}
            {...props}
            variant={variant || 'contained'}
            color={color === 'primary' ? 'primary' : 'secondary'}
            {...rest}
            ref={ref}
          >
            {children}
          </MuiButton>
        );
    }
  }
);

const roleVariants = { primary: 'contained', secondary: 'outlined', tertiary: 'text', destructive: 'text' } as const;

const getButtonRole = (color: ButtonProps['color'], variant: ButtonProps['variant']): ButtonRole => {
  if (color === 'tertiary' || color === 'destructive') {
    return color;
  }
  if (variant === 'text') {
    return 'tertiary';
  }

  return color === 'primary' ? 'primary' : 'secondary';
};

const paintProperties = new Set([
  'background',
  'backgroundColor',
  'backgroundImage',
  'color',
  'border',
  'borderColor',
  'borderWidth',
  'borderStyle',
  'boxShadow',
  'outline',
  'outlineColor',
  'outlineWidth',
  'outlineStyle',
  'outlineOffset',
]);

const getGeometryStyle = (style?: CSSProperties): CSSProperties | undefined =>
  style && Object.fromEntries(Object.entries(style).filter(([property]) => !paintProperties.has(property)));

Button.displayName = 'Button';

Button.defaultProps = {
  loading: false,
  disabled: false,
};
